import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { apiBaseURL } from "@/initializers/axios-client";
import {
  getGetWorkspaceQueryKey,
  getListMatchedStreetsQueryKey,
  getListStravaRunTracksQueryKey,
} from "@/api/orval";

// Handle returned by POST /strava/activities:import, used to open the SSE stream.
export interface ImportHandle {
  importId: string;
  token: string;
  workspaceId: string;
}

export type ImportPhase = "importing" | "retrying" | "complete" | "error";

export interface ImportProgress {
  workspaceId: string;
  phase: ImportPhase;
  index: number;
  total: number;
}

interface ImportProgressContextValue {
  progress: ImportProgress | null;
  startImport: (handle: ImportHandle) => void;
}

const STORAGE_KEY = "active_import";

const ImportProgressContext = createContext<ImportProgressContextValue | null>(
  null,
);

const readStoredHandle = (): ImportHandle | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ImportHandle) : null;
  } catch {
    return null;
  }
};

export const ImportProgressProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const queryClient = useQueryClient();
  // Rehydrate from localStorage so an in-flight import survives a reload (the
  // token outlives a short import).
  const [handle, setHandle] = useState<ImportHandle | null>(readStoredHandle);
  const [progress, setProgress] = useState<ImportProgress | null>(() => {
    const h = readStoredHandle();
    return h
      ? { workspaceId: h.workspaceId, phase: "importing", index: 0, total: 0 }
      : null;
  });
  const lastRefetch = useRef(0);

  const startImport = useCallback((h: ImportHandle) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(h));
    setHandle(h);
    setProgress({
      workspaceId: h.workspaceId,
      phase: "importing",
      index: 0,
      total: 0,
    });
  }, []);

  useEffect(() => {
    if (!handle) return;
    const { importId, token, workspaceId } = handle;

    // Refetch the matched streets + workspace so the map reveals coverage and
    // the stats update. Throttled: the GeoJSON serializer is heavy for big cities.
    const refetch = (force = false) => {
      const now = Date.now();
      if (!force && now - lastRefetch.current < 2000) return;
      lastRefetch.current = now;
      queryClient.invalidateQueries({
        queryKey: getListMatchedStreetsQueryKey({ workspaceId }),
      });
      queryClient.invalidateQueries({
        queryKey: getGetWorkspaceQueryKey({ workspaceId }),
      });
      queryClient.invalidateQueries({
        queryKey: getListStravaRunTracksQueryKey({ workspaceId }),
      });
    };

    const finishTracking = () => {
      localStorage.removeItem(STORAGE_KEY);
      setHandle(null);
    };

    const es = new EventSource(
      `${apiBaseURL}/strava/imports/${importId}/events?token=${encodeURIComponent(
        token,
      )}`,
    );

    es.onmessage = (e) => {
      let event: { type?: string; index?: number; total?: number };
      try {
        event = JSON.parse(e.data);
      } catch {
        return;
      }
      switch (event.type) {
        case "import_started":
          setProgress({
            workspaceId,
            phase: "importing",
            index: 0,
            total: event.total ?? 0,
          });
          break;
        case "run_progress":
          setProgress({
            workspaceId,
            phase: "importing",
            index: event.index ?? 0,
            total: event.total ?? 0,
          });
          refetch();
          break;
        case "retrying":
          setProgress((p) => (p ? { ...p, phase: "retrying" } : p));
          break;
        case "import_complete":
          refetch(true);
          setProgress(null);
          finishTracking();
          es.close();
          break;
        case "error":
          setProgress((p) => (p ? { ...p, phase: "error" } : p));
          finishTracking();
          es.close();
          break;
      }
    };

    return () => es.close();
  }, [handle, queryClient]);

  return (
    <ImportProgressContext.Provider value={{ progress, startImport }}>
      {children}
    </ImportProgressContext.Provider>
  );
};

export const useImportProgress = () => {
  const ctx = useContext(ImportProgressContext);
  if (!ctx) {
    throw new Error(
      "useImportProgress must be used within an ImportProgressProvider",
    );
  }
  return ctx;
};
